import { LandmarkIcon } from 'lucide-react-native';
// This import throws an error, but this is the correct syntax.
import { Image, YStack } from 'tamagui';

import {
    FruitfulCustomIconBorderRadiusMap,
    FruitfulCustomIconShadow,
    type FruitfulCustomIconSize,
    FruitfulCustomIconSizeMap,
} from '../icons/FruitfulCustomIcon';
import { Icon } from '../icons/Icon';
import { SvgUri } from './Svg';

type Size = FruitfulCustomIconSize;

// Need to determine why these values cannot just be the FruitfulCustomIconSizeMap values.
// Perhaps these should be computed values based on FruitfulCustomIconSizeMap values?
const IMAGE_SIZE_MAP: Record<Size, number> = {
    xxs: 20,
    xs: 24,
    sm: 32,
    md: 40,
    lg: 65,
    xl: 76,
};

interface InstitutionLogoProps {
    logo?: string; // Quiltt uri, CloudFront uri, or base64 encoded image for legacy logos
    size?: Size;
    showShadow?: boolean;
}

export const InstitutionLogo = ({
    logo,
    size = 'md',
    showShadow,
}: InstitutionLogoProps) => {
    const logoIsSvgLink = /^(http).+(svg)$/.test(logo ?? '');
    const logoIsPngLink = /^(http).+(png)$/.test(logo ?? ''); // handling logos from Quiltt

    const tileSize = FruitfulCustomIconSizeMap[size];
    const imageSize = logoIsPngLink ? tileSize : IMAGE_SIZE_MAP[size];
    const borderRadius = FruitfulCustomIconBorderRadiusMap[size];
    const shadowStyle = showShadow ? { ...FruitfulCustomIconShadow } : {};

    return (
        <YStack alignItems="center" justifyContent="center">
            {logo != null ? (
                // Outer wrapper is used for showing shadow because inner wrapper cannot show shadow
                // (since it uses 'overflow: hidden' so they border radius can clip the inner image content)
                <YStack
                    alignItems="center"
                    justifyContent="center"
                    width={tileSize}
                    height={tileSize}
                    borderRadius={borderRadius}
                    backgroundColor="$gray100"
                    {...shadowStyle}
                >
                    {logoIsSvgLink ? (
                        <YStack
                            alignItems="center"
                            justifyContent="center"
                            width={tileSize}
                            height={tileSize}
                            overflow="hidden" // this is needed to clip the inner image content so the border radius can be respected
                            borderRadius={borderRadius}
                        >
                            <SvgUri
                                uri={logo}
                                // In order for our desired border radius to be respected versus whatever might be built into the svg,
                                // we need to expand the svg a bit in order for the outer element to trim the corners.
                                width={tileSize + 2}
                                height={tileSize + 2}
                            />
                        </YStack>
                    ) : (
                        <YStack
                            alignItems="center"
                            justifyContent="center"
                            borderRadius={borderRadius}
                            width={imageSize}
                            height={imageSize}
                            overflow="hidden" // this is needed to clip the inner image content so the border radius can be respected
                        >
                            <Image
                                source={{
                                    uri: logoIsPngLink
                                        ? logo
                                        : `data:image/png;base64,${logo}`,
                                }}
                                width={imageSize}
                                height={imageSize}
                                borderRadius={
                                    logoIsPngLink ? borderRadius : undefined
                                }
                            />
                        </YStack>
                    )}
                </YStack>
            ) : (
                <YStack
                    width={tileSize}
                    height={tileSize}
                    backgroundColor="$gray400"
                    borderRadius={borderRadius}
                    alignItems="center"
                    justifyContent="center"
                    {...shadowStyle}
                >
                    <Icon
                        icon={LandmarkIcon}
                        size={tileSize / 2}
                        color="$gray1100"
                        {...(tileSize < 40 ? { strokeWidth: 1.1 } : {})}
                    />
                </YStack>
            )}
        </YStack>
    );
};
